import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRightLeft, GitCompare, ShoppingCart } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchDevices } from "@/lib/api";
import { COMPARISONS } from "@/data/static";

const pkr = (n) => `PKR ${n.toLocaleString("en-PK")}`;

const ROWS = [
  { key: "price_min", label: "Live Price", fmt: (d) => (d.price_min === d.price_max ? pkr(d.price_min) : `${pkr(d.price_min)} - ${pkr(d.price_max)}`), better: (a, b) => a.price_min < b.price_min },
  { key: "chipset", label: "Chipset", fmt: (d) => d.chipset },
  { key: "fps_tier", label: "FPS Tier", fmt: (d) => `${d.fps_tier} FPS`, better: (a, b) => a.fps_tier > b.fps_tier },
  { key: "pubg", label: "PUBG Mobile", fmt: (d) => d.games?.pubg || "Data N/A" },
  { key: "cod", label: "CoD Mobile", fmt: (d) => d.games?.cod || "Data N/A" },
  { key: "freefire", label: "Free Fire", fmt: (d) => d.games?.freefire || "Data N/A" },
  { key: "genshin", label: "Genshin Impact", fmt: (d) => d.games?.genshin || "Data N/A" },
  { key: "display", label: "Display", fmt: (d) => d.display },
  { key: "ram_storage", label: "RAM / Storage", fmt: (d) => d.ram_storage },
  { key: "battery", label: "Battery", fmt: (d) => d.battery },
  { key: "cooling_rating", label: "Cooling", fmt: (d) => d.cooling_rating },
  { key: "pta_status", label: "PTA Status", fmt: (d) => d.pta_status },
];

export default function Compare() {
  const { slugA, slugB } = useParams();
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [a, setA] = useState(slugA || "");
  const [b, setB] = useState(slugB || "");

  useEffect(() => {
    fetchDevices({ sort: "popularity" }).then(setAll).catch(() => {});
  }, []);

  useEffect(() => {
    setA(slugA || "");
    setB(slugB || "");
  }, [slugA, slugB]);

  const deviceA = useMemo(() => all.find((d) => d.slug === a), [all, a]);
  const deviceB = useMemo(() => all.find((d) => d.slug === b), [all, b]);

  const pick = (which, slug) => {
    if (which === "a") setA(slug); else setB(slug);
    const na = which === "a" ? slug : a;
    const nb = which === "b" ? slug : b;
    if (na && nb) navigate(`/compare/${na}/${nb}`);
  };

  const title = deviceA && deviceB ? `${deviceA.name} vs ${deviceB.name}: Which is Better for Gaming?` : "Compare Gaming Phones";

  const jsonLd = deviceA && deviceB ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: `Is ${deviceA.name} better than ${deviceB.name} for gaming?`, acceptedAnswer: { "@type": "Answer", text: `${deviceA.name} runs ${deviceA.chipset} at an estimated ${deviceA.fps_tier} FPS tier, while ${deviceB.name} runs ${deviceB.chipset} at ${deviceB.fps_tier} FPS. Compare live PKR pricing and per-game FPS above before deciding.` } },
    ],
  } : null;

  return (
    <>
      <Seo
        title={title}
        description={deviceA && deviceB ? `${deviceA.name} vs ${deviceB.name} gaming comparison - live PKR price, chipset, per-game FPS, display and cooling.` : "Compare any two gaming phones in Pakistan side by side - live price, chipset, per-game FPS and cooling."}
        path={slugA && slugB ? `/compare/${slugA}/${slugB}` : "/compare"}
        jsonLd={jsonLd}
      />

      <div className="border-b border-border bg-graphite">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="flex items-center gap-2 font-display text-2xl font-black tracking-tight text-white sm:text-4xl">
            <GitCompare className="h-6 w-6 text-neon" /> Compare Gaming Phones
          </h1>
          <p className="mt-2 text-sm text-slate-400 sm:text-base">Pick any two phones for a live side-by-side gaming spec breakdown.</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <DevicePicker testid="compare-select-a" devices={all} value={a} onChange={(v) => pick("a", v)} placeholder="Select phone A" />
          <button
            data-testid="compare-swap-btn"
            onClick={() => { if (a && b) navigate(`/compare/${b}/${a}`); }}
            className="mx-auto flex h-9 w-9 items-center justify-center rounded-full border border-border bg-slateSurface text-slate-400 hover:border-neon/50 hover:text-neon"
            aria-label="Swap"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
          <DevicePicker testid="compare-select-b" devices={all} value={b} onChange={(v) => pick("b", v)} placeholder="Select phone B" />
        </div>

        {deviceA && deviceB ? (
          <div data-testid="compare-table" className="mt-8 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-graphite">
                  <th className="p-3 text-left text-xs font-heading uppercase tracking-wider text-slate-500">Spec</th>
                  <th className="p-3 text-left font-heading text-white">{deviceA.name}</th>
                  <th className="p-3 text-left font-heading text-white">{deviceB.name}</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => {
                  const aWin = r.better && deviceA[r.key] !== undefined ? r.better(deviceA, deviceB) : false;
                  const bWin = r.better && deviceB[r.key] !== undefined ? r.better(deviceB, deviceA) : false;
                  return (
                    <tr key={r.key} data-testid={`compare-row-${r.key}`} className="border-t border-border">
                      <td className="p-3 text-xs font-medium text-slate-500">{r.label}</td>
                      <td className={`p-3 ${aWin ? "font-semibold text-neon" : "text-slate-300"}`}>{r.fmt(deviceA)}</td>
                      <td className={`p-3 ${bWin ? "font-semibold text-neon" : "text-slate-300"}`}>{r.fmt(deviceB)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-border bg-slateSurface p-10 text-center text-slate-400">
            Select two phones above to see a full gaming comparison.
          </div>
        )}

        {deviceA && deviceB && (
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/device/${deviceA.slug}`} className="rounded-lg border border-border bg-slateSurface px-4 py-2.5 text-sm font-heading font-semibold text-white hover:border-cyber/50">View {deviceA.name}</Link>
            <Link to={`/device/${deviceB.slug}`} className="rounded-lg border border-border bg-slateSurface px-4 py-2.5 text-sm font-heading font-semibold text-white hover:border-cyber/50">View {deviceB.name}</Link>
            <a
              href={`https://phoneflip.pk/search?q=${encodeURIComponent(deviceA.name)}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1.5 rounded-lg bg-neon px-4 py-2.5 text-sm font-heading font-bold text-charcoal"
            >
              <ShoppingCart className="h-3.5 w-3.5" /> Buy on PhoneFlip
            </a>
          </div>
        )}

        <section className="mt-10">
          <h2 className="mb-4 font-heading text-lg font-bold text-white">Popular Comparisons</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {COMPARISONS.map((c, i) => (
              <Link
                key={c.title}
                data-testid={`curated-compare-${i}`}
                to={`/compare/${c.a}/${c.b}`}
                className="rounded-lg border border-border bg-slateSurface p-4 transition-colors hover:border-neon/40"
              >
                <div className="font-heading text-sm font-semibold text-white">{c.title}</div>
                <p className="mt-1 text-xs text-slate-400">{c.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-8"><Disclaimer /></div>
      </div>
    </>
  );
}

const DevicePicker = ({ testid, devices, value, onChange, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-11 border-border bg-slateSurface text-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-80 bg-slateSurface text-white">
      {devices.map((d) => (
        <SelectItem key={d.slug} value={d.slug} className="text-white focus:bg-graphite focus:text-neon">
          {d.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
